import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { Text, type DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { measuredUploadSizeBytes, type DatasetUploadStatus } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { UPLOAD_STATUS_LABELS } from "@/components/dataset";
import { formatBytes } from "@/utils";
import {
  formatCommittedEpisodes,
  formatUploadFailure,
  missingChannelNoticeCount,
} from "./datasetUploadFormatters";
import type { DatasetUploadTableRow } from "./DatasetUploadTableRow";
import styles from "./datasetUpload.module.css";

export const datasetUploadColumns: readonly DataTableColumn<DatasetUploadTableRow>[] = [
  {
    key: "display_name",
    header: "이름",
    width: "minmax(280px, 1fr)",
    render: (item) => (
      <div className={styles.nameCell}>
        <Text variant={hatcheryTextModes["body-bold"]}>{item.display_name}</Text>
      </div>
    ),
  },
  {
    key: "status",
    header: "상태",
    width: "150px",
    render: (item) => renderImportStatus(item),
  },
  {
    key: "declared_size_bytes",
    header: "크기",
    width: "110px",
    align: "right",
    // 미실측(0 sentinel)은 "0.0 B" 가 아니라 "—" 로 표시한다. 실측 전 잡과 실제 빈 업로드가
    // 같은 표기를 받으면 안 된다. 값만으로는 구분할 수 없다. 그래서 job 을 읽는 render 를 쓴다.
    render: (item) => formatBytes(measuredUploadSizeBytes(item), "—"),
  },
  {
    key: "modified_at",
    header: "수정일",
    width: "180px",
    visible: false,
    format: (value) => formatDateTime(parseUtcMs(String(value))),
  },
  {
    key: "dataset_uuid",
    header: "Dataset UUID",
    width: "280px",
    visible: false,
    render: (item) => <span className={styles.sourceUuid}>{item.dataset_uuid ?? "-"}</span>,
  },
];

function renderImportStatus(item: DatasetUploadTableRow): ReactNode {
  const meta = resolveStatusMeta(item);
  return (
    <span className={styles.statusCell}>
      <span className={resolveStatusClassName(item.status)}>
        {UPLOAD_STATUS_LABELS[item.status]}
      </span>
      {meta ? (
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.statusMeta}>
          {meta}
        </Text>
      ) : null}
    </span>
  );
}

// Status 셀 보조 텍스트. 실패면 실패 사유를, publishing 이면 서버 진행률(%)과 확정된 에피소드
// 실수치를 함께 보여준다. 그래서 별도 Progress 컬럼 없이도 진행 상태를 표시한다.
function resolveStatusMeta(item: DatasetUploadTableRow): string | null {
  if (item.status === "failed") {
    return formatUploadFailure(item.failure, item.upload_verified);
  }
  if (item.status === "done") {
    // 업로드가 학습에서 제외한 채널 누락 에피소드 수다. 업로드 창의 완료 문구와 같은 판정을 쓴다.
    const missing = missingChannelNoticeCount(item.missing_channel_episode_count);
    return missing === null ? null : `채널 누락 ${missing.toLocaleString()}개 제외`;
  }
  if (item.status !== "publishing") {
    return null;
  }
  const percent =
    item.publish_progress_percent === null ? null : `${Math.round(item.publish_progress_percent)}%`;
  const episodes = formatCommittedEpisodes(item.import_progress);
  return [percent, episodes].filter((part) => part !== null).join(" · ") || null;
}

function resolveStatusClassName(status: DatasetUploadStatus): string {
  const tone =
    status === "done" || status === "duplicate_skipped"
      ? styles.statusSuccess
      : status === "failed" || status === "conflict" || status === "cancelled"
        ? styles.statusDanger
        : styles.statusActive;
  return `${styles.statusBadge} ${tone}`;
}
